/* =====================================================================
   Zodiac shop runtime — mini cart extras, collection card, tools filter,
   holds angle lens. Loaded site-wide because the cart is global.
   ===================================================================== */
(function () {
  'use strict';

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function cartEl() {
    return qs('cart-drawer');
  }

  function capCartLines() {
    var linesWrap = qs('.zshop-cart__lines');
    if (!linesWrap) return;
    var lines = qsa('.zshop-line', linesWrap);
    var h = 0;
    var n = Math.min(2, lines.length);
    for (var i = 0; i < n; i++) h += lines[i].offsetHeight;
    if (h) linesWrap.style.setProperty('--cart-lines-h', h + 'px');
    else linesWrap.style.removeProperty('--cart-lines-h');
    linesWrap.classList.toggle('is-overflow', lines.length > 2);
  }

  function sectionsPayload(cart) {
    if (!cart || !cart.getSectionsToRender) return ['cart-drawer', 'cart-icon-bubble'];
    return cart.getSectionsToRender().map(function (s) { return s.id; });
  }

  function isCompactDrawer(node) {
    return !!(node && (node.querySelector('.zshop-cart__panel') || node.querySelector('.zshop-cart__foot') || node.querySelector('.zshop-cart__x')));
  }

  function fillBubble(html) {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector('.shopify-section') || doc.body;
    if (el) bubble.innerHTML = el.innerHTML;
  }

  /* ---------------------------------------------------------------------
     Do not haul the drawer back open.

     An add opens the mini cart optimistically -- showAddPlaceholder paints a
     pending line and calls openDrawer while /cart/add is still in flight --
     and the repaint lands 80-130ms later. A shopper who dismissed the drawer
     inside that window used to have it forced open again, because fillDrawer
     decided `active` from cart emptiness alone and finish() called openDrawer
     unconditionally. CartDrawer.close already bumps _openGen for exactly this
     reason, but only CartDrawer.open's own setTimeout ever consulted it, so
     the repaint path sailed straight past.

     The baseline is stamped when an add starts and whenever the drawer is
     deliberately opened; a _openGen that has moved past it means the shopper
     shut the drawer since, and the repaint must refresh the contents without
     re-activating. Emptying the cart still closes it.
     --------------------------------------------------------------------- */
  function markDrawerOpen(cart) {
    if (cart) cart._zshopOpenGen = cart._openGen || 0;
  }

  function closedByShopper(cart) {
    return !!cart && (cart._openGen || 0) !== (cart._zshopOpenGen || 0);
  }

  function fillDrawer(html) {
    var cart = cartEl();
    if (!cart || !html) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('cart-drawer.zshop-cart') || doc.querySelector('cart-drawer');
    if (!isCompactDrawer(fresh)) return false;
    var empty = fresh.classList.contains('is-empty');
    cart.classList.toggle('is-empty', empty);
    cart.classList.add('drawer', 'zshop-cart', 'animate');
    if (empty || closedByShopper(cart)) cart.classList.remove('active');
    else cart.classList.add('active');
    cart.innerHTML = fresh.innerHTML;
    var overlay = cart.querySelector('#CartDrawer-Overlay');
    if (overlay && typeof cart.close === 'function') {
      overlay.addEventListener('click', cart.close.bind(cart));
    }
    /* The line above brought in a fresh Checkout button, and Liquid renders
       it enabled. setCartBusy had written `disabled` onto the button this
       repaint just threw away, so without re-asserting it the guard is simply
       gone for whatever is left of the in-flight window -- and a shopper who
       presses Checkout there leaves for the payment page with a cart that is
       still being written. `inflight` is the count that survives a repaint,
       so it, not the DOM, is what the state is rebuilt from. */
    setCartBusy(inflight > 0);
    return true;
  }

  /* ---------------------------------------------------------------------
     The confirmation state.

     After an add, the mini cart answers one question -- "did that work?" --
     and the best answer is the thing that was just added, not a list of
     everything in the cart. assets/zodiac-shop.css hides the other lines
     and swaps "Continue shopping" for "View cart (N)" whenever cart-drawer
     carries `is-added`; the cord picker on the featured line keeps working,
     because changing your mind about the colour is the one edit worth
     making at this moment.

     Two things make this awkward, and both are handled here rather than in
     the callers:

     1. The panel is repainted from a freshly-fetched copy of the section on
        every cart change -- by fillDrawer here, by renderContents in
        assets/cart.js, by patchDrawerChrome -- and each repaint throws away
        the marker class. So the marker is re-applied by a MutationObserver
        on the drawer rather than at each of the call sites, which is the
        only version that cannot be forgotten by a future path.

     2. The key of the line just added is only known at runtime, and there
        are two add paths: this file's addVariant (collection quick-add,
        cross-sell) and Dawn's product-form.js (the PDP). Both are wired in
        below; neither one owns the state.

     The state is left the moment the drawer closes, so opening the cart
     again -- from a merge, or from anywhere else -- shows the whole cart.
     --------------------------------------------------------------------- */
  var addedKey = null;
  var addedSeen = false;
  var addedWatcher = null;

  function paintAdded() {
    var cart = cartEl();
    if (!cart || !addedKey) return;
    var lines = qsa('.zshop-line', cart);
    var featured = lines.filter(function (el) {
      return el.getAttribute('data-key') === String(addedKey);
    })[0];

    /* Not finding the line means one of two opposite things, and addedSeen
       is what tells them apart.

       Never seen it: this paint is running against the cart as it was a
       moment ago -- the key is known before the repaint arrives, on every
       path -- so wait for the repaint that will contain it.

       Seen it and lost it: the line was removed, or merged into another by
       a cord swap. A confirmation with nothing to confirm is an empty
       panel, so fall back to the whole cart. */
    if (!featured) {
      if (addedSeen) exitAdded();
      return;
    }

    addedSeen = true;
    lines.forEach(function (el) { el.classList.toggle('zshop-line--added', el === featured); });
    cart.classList.add('is-added');
  }

  function enterAdded(key) {
    if (!key) return;
    cancelPendingExit();
    addedKey = String(key);
    addedSeen = false;
    paintAdded();
    watchAdded();
  }

  function exitAdded() {
    cancelPendingExit();
    if (!addedKey) return;
    addedKey = null;
    addedSeen = false;
    var cart = cartEl();
    if (!cart) return;
    cart.classList.remove('is-added');
    qsa('.zshop-line--added', cart).forEach(function (el) {
      el.classList.remove('zshop-line--added');
    });
  }

  var exitTimer = null;

  function cancelPendingExit() {
    if (exitTimer) { clearTimeout(exitTimer); exitTimer = null; }
  }

  /* The longest transition on the panel or its wrapper, in ms. Dawn animates
     `transform` on .drawer__inner and `visibility` on .drawer, both over
     --duration-default, and this theme replaces the transform on wide
     viewports -- so read it rather than hard-coding 300. */
  function closeDurationMs(cart) {
    var longest = 0;
    [qs('.drawer__inner', cart), cart].forEach(function (node) {
      if (!node) return;
      var raw;
      try { raw = getComputedStyle(node).transitionDuration; } catch (e) { return; }
      String(raw || '').split(',').forEach(function (part) {
        var n = parseFloat(part);
        if (isNaN(n)) return;
        longest = Math.max(longest, /ms/.test(part) ? n : n * 1000);
      });
    });
    /* Capped, because a stylesheet with a pathological duration should not
       strand the drawer in the confirmation state. +60ms so the repaint lands
       after the panel is gone rather than on the same frame it disappears. */
    return Math.min(longest, 1000) + 60;
  }

  /* Leaving the confirmation state repaints the panel as the whole cart: the
     hidden lines come back and the "Added" head goes. Doing that the moment
     `active` drops meant doing it at the START of the close animation, while
     the panel was still on screen -- so closing a one-line confirmation
     flashed the full cart on the way out. Wait for the panel to actually be
     gone. If it comes back first, the shopper reopened it and there is
     nothing to leave. */
  function exitAddedAfterClose() {
    if (!addedKey || exitTimer) return;
    var cart = cartEl();
    if (!cart) return;
    exitTimer = setTimeout(function () {
      exitTimer = null;
      if (!cart.classList.contains('active')) exitAdded();
    }, closeDurationMs(cart));
  }

  function watchAdded() {
    var cart = cartEl();
    if (!cart || addedWatcher || typeof MutationObserver !== 'function') return;
    /* childList only. Observing attributes here would re-fire on the class
       this very callback writes. */
    addedWatcher = new MutationObserver(function () {
      if (addedKey) paintAdded();
    });
    addedWatcher.observe(cart, { childList: true, subtree: true });

    /* A second, narrow observer for the way out. Dawn's close() drops the
       `active` class; there is no close event to listen for. Re-entry is
       harmless -- exitAdded returns immediately once addedKey is null. */
    new MutationObserver(function () {
      if (!addedKey) return;
      if (cart.classList.contains('active')) cancelPendingExit();
      else exitAddedAfterClose();
    }).observe(cart, { attributes: true, attributeFilter: ['class'] });
  }

  function openDrawer() {
    var cart = cartEl();
    if (!cart) return;
    if (cart.classList.contains('is-empty') && !cart.querySelector('.zshop-line')) return;
    cart.classList.remove('is-empty');
    cart.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    if (typeof cart.open === 'function') cart.open();
    markDrawerOpen(cart);
    requestAnimationFrame(capCartLines);
  }

  var cartLock = Promise.resolve();
  var inflight = 0;

  function setCartBusy(on) {
    var cart = cartEl();
    if (cart) cart.setAttribute('data-zshop-busy', on ? 'true' : 'false');
    var btn = qs('#CartDrawer-Checkout');
    if (!btn) return;
    if (on) btn.disabled = true;
    else if (cart && !cart.classList.contains('is-empty')) btn.disabled = false;
  }

  function withCartLock(fn) {
    inflight += 1;
    setCartBusy(true);
    var run = cartLock.then(fn, fn);
    cartLock = run.then(function () {}, function () {});
    return run.then(
      function (value) {
        inflight = Math.max(0, inflight - 1);
        if (!inflight) setCartBusy(false);
        return value;
      },
      function (err) {
        inflight = Math.max(0, inflight - 1);
        if (!inflight) setCartBusy(false);
        throw err;
      }
    );
  }

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  }

  function cartChangeUrl() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  }

  function throwIfCartError(state) {
    if (!state) return state;
    if (state.status || state.errors) throw state;
    return state;
  }

  function escText(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function showAddPlaceholder(card, variant) {
    var cart = cartEl();
    if (!cart) return;
    var wrap = cart.querySelector('.zshop-cart__lines');
    if (!wrap) return;
    qsa('.zshop-line--pending', wrap).forEach(function (el) { el.remove(); });

    var titleEl = card.querySelector('.zshop-card__title a') || card.querySelector('.zshop-card__title');
    var title = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : 'Item';
    var href = (titleEl && titleEl.getAttribute('href')) || '#';
    var img = card.querySelector('.zshop-card__ph img');
    var src = (variant && variant.image) || (img && (img.currentSrc || img.src)) || '';
    var size = card.getAttribute('data-chosen') || '';

    var line = document.createElement('div');
    line.className = 'zshop-line cart-item zshop-line--pending';
    line.setAttribute('data-pending', 'true');
    line.innerHTML =
      (src
        ? '<a class="zshop-line__thumb" href="' + escText(href) + '" tabindex="-1" aria-hidden="true"><img src="' + escText(src) + '" alt="" width="72" height="54"></a>'
        : '<a class="zshop-line__thumb" href="' + escText(href) + '" tabindex="-1" aria-hidden="true"></a>') +
      '<div><a href="' + escText(href) + '" class="zshop-line__name cart-item__name">' + escText(title) + '</a>' +
      (size ? '<div class="zshop-line__var">' + escText(size) + '</div>' : '') +
      '<div class="zshop-line__tools"><span class="zshop-line__qty-ph">1</span></div></div>';

    wrap.insertBefore(line, wrap.firstChild);
    wrap.scrollTop = 0;

    var items = cart.querySelector('cart-drawer-items');
    if (items) items.classList.remove('is-empty');

    /* The placeholder is the confirmation, a beat early. Giving it a
       throwaway key and featuring it means the shopper sees the
       confirmation state immediately and it simply gains the real line when
       refreshFromAdd calls enterAdded with the key the server assigned --
       rather than flashing the whole cart in between. */
    line.setAttribute('data-key', 'zshop-pending');
    enterAdded('zshop-pending');

    openDrawer();
  }

  function dropAddPlaceholder() {
    qsa('.zshop-line--pending').forEach(function (el) { el.remove(); });
  }

  function loadDrawerHtml() {
    return fetch('/?sections=cart-drawer,cart-icon-bubble', {
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function syncCartPage() {
    var pageCart = document.querySelector('#MainContent cart-items');
    if (pageCart && typeof pageCart.refreshCartPage === 'function') {
      pageCart.refreshCartPage();
    }
  }

  function refreshFromAdd(state) {
    var count = state && typeof state.item_count === 'number' ? state.item_count : null;
    /* Before the paint, so the first frame the shopper sees is already the
       confirmation and not the full cart flicking over to it. */
    enterAdded((addedLine(state) || {}).key);
    var painted = fillDrawer(state && state.sections && state.sections['cart-drawer']);
    fillBubble(state && state.sections && state.sections['cart-icon-bubble']);
    function finish(ok) {
      var cart = cartEl();
      if (count === 0) {
        if (cart && typeof cart.close === 'function') cart.close();
        return;
      }
      if (ok && !closedByShopper(cart)) openDrawer();
      syncCartPage();
    }
    if (painted) {
      finish(true);
      return Promise.resolve();
    }
    return loadDrawerHtml().then(function (data) {
      fillDrawer(data && data['cart-drawer']);
      fillBubble(data && data['cart-icon-bubble']);
      finish(true);
    }).catch(function () {
      finish(false);
    });
  }

  function postAdd(id, quantity, silent) {
    var cart = cartEl();
    var formData = new FormData();
    formData.append('id', String(id));
    formData.append('quantity', String(quantity || 1));
    if (!silent) {
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', '/');
    }
    if (cart && typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(document.activeElement);
    }
    /* The reference point for closedByShopper: anything that closes the drawer
       from here until the repaint is the shopper deciding they are done. */
    markDrawerOpen(cart);
    return fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: formData
    }).then(function (r) {
      return r.json().then(function (state) {
        if (!r.ok) throw state;
        return throwIfCartError(state);
      });
    });
  }

  function addVariant(id, quantity, silent) {
    var extra = window._zshopAddProps || null;
    window._zshopAddProps = null;
    var work = function () {
      return postAdd(id, quantity, silent).then(function (state) {
        if (!silent && window.zodiacPosthog) window.zodiacPosthog.added(state, extra);
        if (silent) return state;
        return refreshFromAdd(state).then(function () { return state; });
      });
    };
    return silent ? work() : withCartLock(work);
  }

  function changeLine(opts) {
    var body = {
      quantity: opts.quantity
    };
    /* Key only. There was an `opts.line` branch here with no caller; leaving
       it meant a future call could address a cart line by position, which is
       unsafe while the cart page renders reversed. */
    if (opts.id) body.id = opts.id;
    if (opts.properties) body.properties = opts.properties;
    if (!opts.silent) {
      body.sections = ['cart-drawer', 'cart-icon-bubble'];
      body.sections_url = '/';
    }
    return fetch(cartChangeUrl(), {
      method: 'POST',
      /* Accept: application/json is load-bearing. routes.cart_change_url is
         /cart/change, not /cart/change.js, and without this header a SUCCESSFUL
         change answers 302 -> /cart, so r.json() rejects on "<!doctype". The
         change had already happened, which is the worst shape of bug: the cart
         was right and the screen was wrong, and the cord chip snapped back to
         the colour the shopper had just replaced. */
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (state) {
        if (!r.ok) throw state;
        return throwIfCartError(state);
      });
    }).then(function (state) {
      if (opts.silent) return state;
      return refreshFromAdd(state).then(function () { return state; });
    });
  }

  /* ---- mini cart: two-line cap + cord swap -------------------------- */
  function parseJSON(el) {
    if (!el) return null;
    var raw = el.getAttribute && el.getAttribute('data-cord-map');
    if (!raw) raw = el.textContent;
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function cordOptionIndex(product, optionName) {
    var want = String(optionName || 'Cord Color').toLowerCase();
    var options = product.options || [];
    var i;
    for (i = 0; i < options.length; i++) {
      var n = String(options[i].name || options[i] || '').toLowerCase();
      if (n === want || n.indexOf('cord') !== -1) return i;
    }
    return -1;
  }

  function variantForCord(product, map, value) {
    var idx = cordOptionIndex(product, map.option_name);
    if (idx < 0) return null;
    var current = (product.variants || []).filter(function (v) {
      return String(v.id) === String(map.variant_id);
    })[0];
    return (product.variants || []).filter(function (v) {
      if (v.available === false) return false;
      if (v.options[idx] !== value) return false;
      if (!current) return true;
      return v.options.every(function (ov, i) {
        return i === idx || ov === current.options[i];
      });
    })[0] || null;
  }

  function addedLine(state) {
    if (!state) return null;
    if (state.items && state.items[0]) return state.items[0];
    if (state.key) return state;
    return state;
  }

  function patchCordLine(wrap, map, added, newId) {
    /* .zcart-line is the cart page's own line card; the chips and the map
       are the same snippet on both surfaces. */
    var line = wrap && wrap.closest('.zshop-line, .zcart-line');
    var row = addedLine(added) || {};
    var newKey = row.key || map.key;
    var image = row.image || row.featured_image;
    var src = typeof image === 'string' ? image : (image && (image.src || image.url));
    map.variant_id = newId;
    map.key = newKey;
    var mapEl = wrap.querySelector('[data-cord-map]');
    if (mapEl) mapEl.textContent = JSON.stringify(map);
    if (line && newKey) {
      line.setAttribute('data-key', newKey);
      var remove = line.querySelector('cart-remove-button');
      if (remove) remove.setAttribute('data-key', newKey);
      var input = line.querySelector('.quantity__input');
      if (input) {
        input.setAttribute('data-quantity-variant-id', String(newId));
        input.setAttribute('data-line-key', newKey);
      }
    }
    if (src && line) {
      var img = line.querySelector('.zshop-line__thumb img');
      if (img) img.src = src;
    }
    wrap.removeAttribute('data-busy');
  }

  function patchDrawerChrome() {
    return fetch('/?sections=cart-drawer,cart-icon-bubble', {
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); }).then(function (data) {
      var html = data && data['cart-drawer'];
      if (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshFoot = doc.querySelector('[data-zshop-cart-foot]');
        var foot = qs('[data-zshop-cart-foot]');
        if (freshFoot && foot) {
          foot.innerHTML = freshFoot.innerHTML;
          /* The footer is where #CartDrawer-Checkout lives, so this repaint
             drops the busy guard exactly as fillDrawer's does. Same rebuild,
             same reason. */
          setCartBusy(inflight > 0);
        }
      }
      fillBubble(data && data['cart-icon-bubble']);
    });
  }

  function applyCordVariant(map, newId, wrap) {
    if (!newId || String(newId) === String(map.variant_id)) return Promise.resolve();
    /* onPage is the cart page; anything else is the drawer. It decides which
       lines count as "the same line twice" below, and whether the drawer is
       allowed to come forward.

       NEITHER cart renders a picker today -- both print the cord instead, see
       mockups/cart/_cord-picker-on-cart-page.md -- so nothing reaches this
       function at all. It is read off the chip's own position, not the URL,
       so a drawer swap made while standing on /cart is still correctly the
       drawer. Kept whole with the snippet it drives: this is the guard that
       stopped the drawer popping over the page, and it would have to be
       rediscovered if the picker came back without it. */
    var onPage = !!(wrap && wrap.closest('#MainContent'));
    var line = wrap && wrap.closest('.zshop-line, .zcart-line');
    var input = line && line.querySelector('.quantity__input');
    var qty = Number(input && input.value) || Number(map.quantity) || 1;
    var oldKey = map.key;
    return withCartLock(function () {
      return postAdd(newId, qty, true).then(function (added) {
        return changeLine({ id: oldKey, quantity: 0, silent: true }).then(function () {
          return added;
        });
      }).then(function (added) {
        var row = addedLine(added) || {};
        var newKey = row.key;
        /* Only the lines on the SAME surface can be the merge partner. The
           drawer's markup is in the DOM of every page, so searching
           .zshop-line from the cart page found the drawer's own copy of the
           line just swapped, called that a merge, and opened the drawer over
           the page the shopper was working in. */
        var other = newKey && qsa(onPage ? '.zcart-line' : '.zshop-line').filter(function (el) {
          return el !== line && el.getAttribute('data-key') === String(newKey);
        })[0];
        if (other) {
          /* The featured line just merged into another one, so the
             confirmation should follow the merge rather than lose its
             subject. Outside the confirmation state this is a no-op. */
          if (addedKey && String(addedKey) === String(oldKey) && newKey) enterAdded(newKey);
          return loadDrawerHtml().then(function (data) {
            fillDrawer(data && data['cart-drawer']);
            fillBubble(data && data['cart-icon-bubble']);
            /* A real merge on the cart page is repainted in place. The drawer
               only opens for a swap the shopper made IN the drawer. */
            if (!onPage) openDrawer();
            syncCartPage();
          });
        }
        if (addedKey && String(addedKey) === String(oldKey)) {
          var swapped = (addedLine(added) || {}).key;
          if (swapped) addedKey = String(swapped);
        }
        patchCordLine(wrap, map, added, newId);
        /* On the cart page the chip changed a variant, which changes the
           line key, the price and the rail. Patching the chrome is enough
           for the drawer; the page has to be repainted from the server. */
        return patchDrawerChrome().then(syncCartPage, syncCartPage);
      });
    });
  }

  function onCordClick(chip) {
    if (chip.getAttribute('aria-pressed') === 'true') return;
    var wrap = chip.closest('.zshop-cord');
    if (!wrap || wrap.getAttribute('data-busy') === 'true') return;
    var map = parseJSON(wrap.querySelector('[data-cord-map]'));
    if (!map) return;
    var value = chip.getAttribute('data-value');
    var chosen = (map.options || []).filter(function (o) { return o.value === value; })[0];
    var mappedId = chosen && chosen.variant_id;
    var previous = wrap.querySelector('.zshop-cord__chip[aria-pressed="true"]');

    wrap.setAttribute('data-busy', 'true');
    wrap.querySelectorAll('.zshop-cord__chip').forEach(function (c) {
      c.setAttribute('aria-pressed', String(c === chip));
    });

    function fail(err) {
      if (err) console.error(err);
      wrap.querySelectorAll('.zshop-cord__chip').forEach(function (c) {
        c.setAttribute('aria-pressed', String(c === previous));
      });
      wrap.removeAttribute('data-busy');
    }

    var swap = mappedId && String(mappedId) !== String(map.variant_id)
      ? Promise.resolve(mappedId)
      : map.handle
        ? fetch('/products/' + map.handle + '.js')
            .then(function (r) { return r.json(); })
            .then(function (product) {
              var match = variantForCord(product, map, value);
              return match && match.id;
            })
        : Promise.resolve(null);

    swap.then(function (id) {
      if (!id || String(id) === String(map.variant_id)) {
        fail();
        return;
      }
      return applyCordVariant(map, id, wrap);
    }).catch(fail);
  }

  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('.zshop-cord__chip');
    if (chip) {
      e.preventDefault();
      e.stopPropagation();
      if (chip.disabled || chip.getAttribute('aria-disabled') === 'true') return;
      onCordClick(chip);
    }
  });

  function bindCart() {
    capCartLines();
    var drawer = cartEl();
    if (!drawer || drawer._zshopBound) return;
    drawer._zshopBound = true;
    drawer.addEventListener('click', function (e) {
      if (e.target && e.target.id === 'CartDrawer-Overlay' && typeof drawer.close === 'function') {
        drawer.close();
      }
    });
    drawer.renderContents = function (state) {
      refreshFromAdd(state);
    };
  }

  window.addEventListener('resize', capCartLines);
  if (window.subscribe && window.PUB_SUB_EVENTS) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
      requestAnimationFrame(function () {
        capCartLines();
        var drawer = cartEl();
        var count = event && event.cartData && event.cartData.item_count;
        if (drawer && count === 0 && typeof drawer.close === 'function') {
          drawer.close();
        }
      });
    });
  }

  /* ---- collection card: size pick + add ----------------------------- */
  function cardData(card) {
    return parseJSON(card.querySelector('[data-zshop-card]'));
  }

  function variantFor(data, size) {
    if (!data || !data.variants) return null;
    var matches = data.variants.filter(function (v) {
      if (!v.available) return false;
      if (size && v.size && v.size !== size) return false;
      return true;
    });
    var prefer = data.firstCord || null;
    var withCord = prefer
      ? matches.filter(function (v) { return v.cord && String(v.cord) === String(prefer); })
      : [];
    var pool = withCord.length ? withCord : matches;
    var flat = pool.filter(function (v) {
      if (!v.incut) return true;
      var s = String(v.incut).toLowerCase();
      return s.indexOf('0 degree') !== -1 || s.indexOf('flat') !== -1;
    });
    return (flat[0] || pool[0] || null);
  }

  function showVariantImage(card, variant) {
    var img = card.querySelector('.zshop-card__ph img');
    if (!img) return;
    var src = variant && variant.image ? variant.image : img.getAttribute('data-default-src');
    if (!src) return;
    img.src = src;
    img.removeAttribute('srcset');
  }

  /* A picked size is a picked variant, so the card's own links have to carry
     it. Without this, choosing 20mm and then tapping the photo lands on the
     PDP showing whichever variant Shopify defaults to -- the choice is made
     and then silently dropped between two clicks on the same card. The clean
     URL is parked in data-href on first use so repeated picks re-write rather
     than stack query strings. */
  function linkVariant(card, variantId) {
    qsa('.zshop-card__media, .zshop-card__title a', card).forEach(function (link) {
      var base = link.getAttribute('data-href');
      if (base === null) {
        base = (link.getAttribute('href') || '').split('?')[0];
        link.setAttribute('data-href', base);
      }
      link.setAttribute('href', variantId
        ? base + '?variant=' + encodeURIComponent(variantId)
        : base);
    });
  }

  /* Apply a chip as the card's selection. Two callers: the shopper tapping
     a chip, and the card settling on its default at boot. Only the first
     swaps the photo. The featured image is the face the merchant chose for
     the product; a variant that happens to carry its own photo (the Alpha
     Librae volumes, one angle per size) must not replace it before anyone
     has asked, or the card shows a picture nobody picked. */
  function cardListContext(card) {
    var m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    var listId = (m && m[1]) || 'shop';
    var category = 'shop';
    if (card && card.closest('[data-zshop-tools]')) category = 'tools';
    else if (card && card.closest('[data-zshop-holds]')) category = 'holds';
    return { list_id: listId, category: category };
  }

  function cardProductProps(card) {
    var title = card && card.querySelector('.zshop-card__title');
    var ctx = cardListContext(card);
    return {
      list_id: ctx.list_id,
      category: ctx.category,
      product_id: card && card.getAttribute('data-ph-product-id') || undefined,
      handle: card && card.getAttribute('data-ph-handle') || undefined,
      name: title ? title.textContent.trim() : undefined
    };
  }

  function choosePick(card, pick, byShopper) {
    if (!card || !pick) return;
    var pickId = pick.getAttribute('data-variant-id');
    if (!pickId) return;
    var fromSize = card.getAttribute('data-chosen');
    var toSize = pick.getAttribute('data-value');
    card.setAttribute('data-chosen', toSize);
    card.setAttribute('data-variant-id', pickId);
    linkVariant(card, pickId);
    qsa('[data-pick]', card).forEach(function (b) {
      b.setAttribute('aria-pressed', String(b === pick));
    });
    if (byShopper) {
      showVariantImage(card, variantFor(cardData(card), toSize));
      var picks = Number(card.getAttribute('data-ph-picks') || 0) + 1;
      card.setAttribute('data-ph-picks', String(picks));
      card.setAttribute('data-ph-shopper-pick', '1');
      if (!card.getAttribute('data-ph-first-pick')) {
        card.setAttribute('data-ph-first-pick', String(Date.now()));
      }
      if (window.zodiacPosthog) {
        var def = card.getAttribute('data-ph-default-size') || '';
        var props = cardProductProps(card);
        props.from_size = fromSize || undefined;
        props.to_size = toSize || undefined;
        props.variant_id = pickId;
        props.pick_count = picks;
        props.changed_from_default = !!(def && toSize && def !== toSize);
        props.switched = !!(fromSize && toSize && fromSize !== toSize);
        props.redundant = !!(fromSize && toSize && fromSize === toSize);
        window.zodiacPosthog.capture('Card Variant Selected', props);
      }
    }
  }

  /* The card renders with its first buyable size already marked (see
     snippets/card-product-shop.liquid). This carries that mark into the
     state the rest of the JS reads -- data-chosen, data-variant-id, the
     ?variant= on the title link -- so an Add pressed before any chip is
     touched adds the size the card is showing. The photo stays the
     featured image; see choosePick. */
  function seedPicks(root) {
    qsa('.zshop-card', root).forEach(function (card) {
      if (card.getAttribute('data-chosen')) return;
      var chips = qsa('[data-pick]', card).filter(function (b) {
        return !b.disabled && b.getAttribute('aria-disabled') !== 'true' && b.getAttribute('data-variant-id');
      });
      if (!chips.length) return;
      var marked = chips.filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })[0];
      choosePick(card, marked || chips[0]);
      if (!card.getAttribute('data-ph-default-size')) {
        card.setAttribute('data-ph-default-size', card.getAttribute('data-chosen') || '');
      }
    });
  }

  document.addEventListener('click', function (e) {
    var expand = e.target.closest && e.target.closest('[data-expand]');
    if (expand) {
      e.preventDefault();
      var cardEx = expand.closest('.zshop-card');
      if (!cardEx) return;
      qsa('.zshop-sz.is-extra', cardEx).forEach(function (sz) { sz.hidden = false; });
      expand.hidden = true;
      if (window.zodiacPosthog) {
        var extra = cardEx.querySelectorAll('.zshop-sz.is-extra').length;
        var expProps = cardProductProps(cardEx);
        expProps.extra_count = extra;
        window.zodiacPosthog.capture('Card Sizes Expanded', expProps);
      }
      return;
    }

    var pick = e.target.closest && e.target.closest('[data-pick]');
    if (pick) {
      e.preventDefault();
      if (pick.disabled || pick.getAttribute('aria-disabled') === 'true') {
        if (window.zodiacPosthog) {
          var blocked = pick.closest('.zshop-card');
          var blockedProps = cardProductProps(blocked);
          blockedProps.to_size = pick.getAttribute('data-value') || undefined;
          window.zodiacPosthog.capture('Card Variant Blocked', blockedProps);
        }
        return;
      }
      choosePick(pick.closest('.zshop-card'), pick, true);
      return;
    }

    var add = e.target.closest && e.target.closest('[data-add]');
    if (add && add.closest('.zshop-card')) {
      e.preventDefault();
      if (add.disabled || add.getAttribute('aria-disabled') === 'true') return;
      var cardA = add.closest('.zshop-card');
      var id = cardA.getAttribute('data-variant-id') || add.getAttribute('data-variant-id');
      if (!id) {
        var data = cardData(cardA);
        var size = cardA.getAttribute('data-chosen') || null;
        var variant = variantFor(data, size);
        id = variant && variant.id;
      }
      if (!id) return;
      var dataA = cardData(cardA);
      var sizeA = cardA.getAttribute('data-chosen') || null;
      var variantA = variantFor(dataA, sizeA);
      var firstPick = Number(cardA.getAttribute('data-ph-first-pick') || 0);
      window._zshopAddProps = {
        size: sizeA || undefined,
        handle: cardA.getAttribute('data-ph-handle') || undefined,
        pick_count: Number(cardA.getAttribute('data-ph-picks') || 0),
        size_changed: cardA.getAttribute('data-ph-shopper-pick') === '1',
        source: 'shop-card',
        ms_after_pick: firstPick ? Date.now() - firstPick : undefined
      };
      showAddPlaceholder(cardA, variantA);
      add.setAttribute('aria-disabled', 'true');
      addVariant(id, 1).catch(function (err) {
        console.error(err);
        dropAddPlaceholder();
        var wrap = qs('.zshop-cart__lines');
        if (wrap && !qsa('.zshop-line:not(.zshop-line--pending)', wrap).length) {
          var cart = cartEl();
          if (cart) {
            cart.classList.add('is-empty');
            if (typeof cart.close === 'function') cart.close();
          }
        }
      }).then(function () {
        add.removeAttribute('aria-disabled');
      });
    }
  });

  /* ---- tools grouped filter ----------------------------------------- */
  function initTools(root) {
    if (!root || root._zshopTools) return;
    root._zshopTools = true;

    var groups = qsa('[data-zshop-group]', root);
    var tiles = qsa('[data-moment]', root);
    var first = tiles[0] && tiles[0].getAttribute('data-moment');
    var selected = [];
    var toolsReady = false;
    var toolsFilterTimer;

    function visibleCount() {
      var n = 0;
      groups.forEach(function (g) {
        if (!g.classList.contains('is-hidden')) {
          n += Number(g.getAttribute('data-count') || 0);
        }
      });
      return n;
    }

    function totalCount() {
      var n = 0;
      groups.forEach(function (g) { n += Number(g.getAttribute('data-count') || 0); });
      return n;
    }

    function render() {
      tiles.forEach(function (t) {
        t.setAttribute('aria-pressed', String(selected.indexOf(t.getAttribute('data-moment')) >= 0));
      });
      groups.forEach(function (g) {
        var id = g.getAttribute('data-zshop-group');
        var always = g.getAttribute('data-always') === 'true';
        var show = !selected.length || always || selected.indexOf(id) >= 0;
        g.classList.toggle('is-hidden', !show);
      });

      var chips = qs('[data-zshop-chips]', root);
      var viewGuided = qs('[data-view="guided"]', root);
      var viewAll = qs('[data-view="all"]', root);
      var countEl = qs('[data-zshop-count]', root);
      var sheetState = qs('[data-sheet-state]', root);
      var tail = qs('[data-clear-all]', root);
      var vis = visibleCount();
      var all = totalCount();

      if (viewGuided) {
        if (selected.length) viewGuided.setAttribute('aria-current', 'true');
        else viewGuided.removeAttribute('aria-current');
        var em = viewGuided.querySelector('em');
        if (em) em.textContent = String(vis);
      }
      if (viewAll) {
        if (!selected.length) viewAll.setAttribute('aria-current', 'true');
        else viewAll.removeAttribute('aria-current');
        var emAll = viewAll.querySelector('em');
        if (emAll) emAll.textContent = String(all);
      }

      if (chips) {
        qsa('[data-clear]', chips).forEach(function (c) { c.remove(); });
        selected.forEach(function (id) {
          var tile = tiles.filter(function (t) { return t.getAttribute('data-moment') === id; })[0];
          if (!tile) return;
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'zshop-chip';
          b.setAttribute('data-clear', id);
          b.innerHTML = (tile.getAttribute('data-label') || id) + ' <i>×</i>';
          chips.appendChild(b);
        });
      }

      if (countEl) {
        countEl.innerHTML = selected.length
          ? '<b>' + vis + '</b> of ' + all
          : '<b>' + all + '</b> tools';
      }
      if (sheetState) {
        if (!selected.length) sheetState.textContent = 'All ' + all;
        else if (selected.length === 1) {
          var t0 = tiles.filter(function (t) { return t.getAttribute('data-moment') === selected[0]; })[0];
          sheetState.textContent = (t0 && t0.getAttribute('data-label')) + ' · ' + vis;
        } else sheetState.textContent = selected.length + ' picked · ' + vis;
      }
      if (tail) tail.hidden = !selected.length;

      if (toolsReady && window.zodiacPosthog) {
        clearTimeout(toolsFilterTimer);
        toolsFilterTimer = setTimeout(function () {
          var list = (window.location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || 'tools';
          window.zodiacPosthog.listFiltered({
            list_id: list,
            category: 'tools',
            filters: selected.map(function (id) {
              var tile = tiles.filter(function (t) { return t.getAttribute('data-moment') === id; })[0];
              return {
                type: 'moment',
                value: id,
                label: (tile && tile.getAttribute('data-label')) || id
              };
            }),
            products: window.zodiacPosthog.productsFromRoot(root),
            result_count: vis
          });
        }, 400);
      }
    }

    root.addEventListener('click', function (e) {
      var tile = e.target.closest('[data-moment]');
      if (tile && root.contains(tile)) {
        var id = tile.getAttribute('data-moment');
        var i = selected.indexOf(id);
        if (i < 0) selected.push(id); else selected.splice(i, 1);
        render();
        return;
      }
      var clear = e.target.closest('[data-clear]');
      if (clear) {
        selected = selected.filter(function (id) { return id !== clear.getAttribute('data-clear'); });
        render();
        return;
      }
      var view = e.target.closest('[data-view]');
      if (view) {
        e.preventDefault();
        selected = view.getAttribute('data-view') === 'all' ? [] : (first ? [first] : []);
        render();
        return;
      }
      if (e.target.closest('[data-clear-all]')) {
        e.preventDefault();
        selected = [];
        render();
        return;
      }
      var bar = e.target.closest('[data-sheet-bar]');
      if (bar) {
        var step = bar.closest('.zshop-picker__step');
        var open = step && step.classList.toggle('is-open');
        bar.setAttribute('aria-expanded', String(!!open));
      }
    });

    render();
    toolsReady = true;
  }

  /* ---- holds angle lens --------------------------------------------- */
  /* The bands, spelled as /pages/how-we-rate-our-holds spells them. */
  var VGRADE = {
    'Easy': 'V2–V4',
    'Moderate': 'V5–V7',
    'Hard': 'V7–V10',
    'Very Hard': 'V10+',
    'Not Recommended': '—'
  };

  function gradeOf(card, angle) {
    if (!angle) return null;
    return card.getAttribute('data-grade-' + angle) || null;
  }

  function initHolds(root) {
    if (!root || root._zshopHolds) return;
    root._zshopHolds = true;
    var state = { angle: null, levels: [] };
    var holdsReady = false;
    var holdsFilterTimer;

    function emitHoldsFilter(match) {
      if (!window.zodiacPosthog) return;
      clearTimeout(holdsFilterTimer);
      holdsFilterTimer = setTimeout(function () {
        var filters = [];
        if (state.angle) filters.push({ type: 'angle', value: String(state.angle) });
        state.levels.forEach(function (lv) {
          filters.push({ type: 'level', value: lv });
        });
        var list = (window.location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || 'holds';
        window.zodiacPosthog.listFiltered({
          list_id: list,
          category: 'holds',
          filters: filters,
          products: window.zodiacPosthog.productsFromRoot(root),
          result_count: match
        });
      }, 400);
    }

    function render() {
      var match = 0;
      qsa('[data-zshop-angle]', root).forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-zshop-angle') === state.angle));
      });
      qsa('[data-zshop-level]', root).forEach(function (b) {
        var lv = b.getAttribute('data-zshop-level');
        var n = 0;
        if (state.angle) {
          qsa('.zshop-card[data-grade-' + state.angle + ']', root).forEach(function (c) {
            if (c.getAttribute('data-grade-' + state.angle) === lv) n += 1;
          });
        }
        b.disabled = !state.angle || n === 0;
        b.setAttribute('aria-pressed', String(state.levels.indexOf(lv) > -1));
        var nn = b.querySelector('.zshop-cell__n');
        if (nn) nn.textContent = state.angle ? String(n) : '';
      });
      var clear = qs('[data-zshop-clear]', root);
      if (clear) clear.hidden = !state.angle;

      qsa('.zshop-card[data-hold]', root).forEach(function (card) {
        var g = gradeOf(card, state.angle);
        var badge = card.querySelector('[data-grade-badge]');
        if (badge) {
          if (!g) {
            badge.hidden = true;
          } else {
            badge.hidden = false;
            badge.classList.toggle('zshop-card__badge--no', g === 'Not Recommended');
            var label = badge.querySelector('[data-grade-label]');
            if (label) {
              label.textContent = g === 'Not Recommended'
                ? 'Not for ' + state.angle + '°'
                : (VGRADE[g] || g);
            }
            var gly = badge.querySelector('[data-grade-glyph]');
            if (gly) {
              qsa('[data-level]', gly).forEach(function (el) {
                el.hidden = el.getAttribute('data-level') !== g;
              });
            }
          }
        }
        var inAnswer = !state.levels.length || state.levels.indexOf(g) > -1;
        card.classList.toggle('zshop-card--off', !inAnswer);
        if (inAnswer) match += 1;
      });

      var empty = qs('[data-zshop-empty]', root);
      var handsGrid = qs('#zshop-g-hands .zshop-grid', root);
      var noneMatch = state.angle && state.levels.length > 0 && match === 0;
      if (empty) {
        empty.hidden = !noneMatch;
        if (noneMatch) {
          var band = empty.querySelector('[data-zshop-empty-band]');
          var ang = empty.querySelector('[data-zshop-empty-angle]');
          if (band) {
            band.textContent = state.levels.map(function (lv) { return VGRADE[lv] || lv; }).join(' / ');
          }
          if (ang) ang.textContent = state.angle;
        }
      }
      if (handsGrid) handsGrid.classList.toggle('is-empty', noneMatch);
      if (holdsReady) emitHoldsFilter(match);
    }

    /* ---- the hold finder's hand-off ---------------------------------
       The finder on /pages/hold-finder cannot filter this page with a
       Shopify filter URL -- the grid here is not a filterable one, it is
       this lens. So it passes its picks as angle=55&level=Hard and the
       lens opens on them. Empty bands stay on the lens so a pick with
       nothing in it is an empty state, not a silent show-everything. */
    function hydrate() {
      var q;
      try { q = new URLSearchParams(window.location.search); } catch (err) { return false; }
      var angle = q.get('angle');
      if (!angle) return false;
      var offered = qsa('[data-zshop-angle]', root).map(function (b) {
        return b.getAttribute('data-zshop-angle');
      });
      if (offered.indexOf(angle) < 0) return false;
      state.angle = angle;

      var levels = [];
      q.getAll('level').forEach(function (raw) {
        String(raw).split(',').forEach(function (v) {
          v = v.trim();
          if (!v || !VGRADE[v] || levels.indexOf(v) > -1) return;
          levels.push(v);
        });
      });
      state.levels = levels;
      return true;
    }

    root.addEventListener('click', function (e) {
      var ang = e.target.closest('[data-zshop-angle]');
      if (ang) {
        var a = ang.getAttribute('data-zshop-angle');
        state.angle = state.angle === a ? null : a;
        if (!state.angle) state.levels = [];
        render();
        return;
      }
      var lv = e.target.closest('[data-zshop-level]');
      if (lv) {
        var v = lv.getAttribute('data-zshop-level');
        var i = state.levels.indexOf(v);
        if (i > -1) {
          state.levels.splice(i, 1);
          render();
          return;
        }
        if (lv.disabled) return;
        state.levels.push(v);
        render();
        return;
      }
      if (e.target.closest('[data-zshop-clear]')) {
        state = { angle: null, levels: [] };
        render();
      }
    });

    var fromUrl = hydrate();
    render();
    holdsReady = true;
    if (fromUrl) {
      emitHoldsFilter(
        qsa('.zshop-card[data-hold]:not(.zshop-card--off)', root).length
      );
      var finder = qs('.zshop-finder', root) || root;
      requestAnimationFrame(function () {
        finder.scrollIntoView({ block: 'start' });
      });
    }
  }

  /* ---------------------------------------------------------------------
     The jump nav.

     Three groups on one page and a hero tall enough that the second one is
     never on screen at load. The nav is anchors, so it works with no JS at
     all -- what JS adds is which link is lit, and taking a link away when
     the finder hides the group under it.
     --------------------------------------------------------------------- */
  function jumpTargets(root) {
    return qsa('[data-zshop-jump-to]', root).map(function (link) {
      return { link: link, group: document.getElementById(link.getAttribute('data-zshop-jump-to')) };
    }).filter(function (t) { return !!t.group; });
  }

  function syncJump(root) {
    var nav = qs('[data-zshop-jump]', root);
    if (!nav) return;
    /* The shell's own height, for scroll-margin-top. Measured here rather
       than assumed: it is one row or two depending on whether the finder is
       switched on, and it reflows when the nav scrolls out of a phone. */
    var shell = qs('.zshop-finder', root);
    if (shell) {
      document.documentElement.style.setProperty(
        '--zshop-shell-h', Math.round(shell.getBoundingClientRect().height) + 'px'
      );
    }

    /* The line a group's top has to cross to count as the one being read.

       Not the bottom edge of the shell. A group jumped to parks below that
       edge by its scroll-margin, so an edge test never sees it arrive and
       lights the group before it -- click Foot holds, watch Hand holds stay
       lit. Nor is the gap a fixed 16px to add back: the sticky header
       collapses DURING a smooth scroll, so the margin the browser scrolled
       by was measured against a taller header than the one that ends up on
       screen, and on a phone the group lands ~105px low.

       A band under the shell absorbs both. A quarter of what is left of the
       viewport reads as "the group you are looking at" at any header height
       and on any screen, and is what the jump's own overshoot lands inside. */
    var base = shell ? shell.getBoundingClientRect().bottom
                     : (parseFloat(getComputedStyle(document.documentElement)
                         .getPropertyValue('--zshop-sticky-top')) || 0);
    var line = base + Math.max(24, (window.innerHeight - base) * 0.25);

    /* Last group whose top has passed under the shell wins. Reading down
       rather than up means the bottom of the page settles on the last
       group instead of flickering between it and the one before. */
    var active = null;
    jumpTargets(root).forEach(function (t) {
      var hidden = t.group.classList.contains('is-hidden');
      t.link.hidden = hidden;
      if (hidden) return;
      if (t.group.getBoundingClientRect().top - line <= 1) active = t.link;
      if (!active) active = t.link;
    });
    qsa('[data-zshop-jump-to]', root).forEach(function (l) {
      if (l === active) l.setAttribute('aria-current', 'true');
      else l.removeAttribute('aria-current');
    });
  }

  function initJump(root) {
    var nav = qs('[data-zshop-jump]', root);
    if (!nav || nav._zshopJump) return;
    nav._zshopJump = true;

    nav.addEventListener('click', function (e) {
      var link = e.target.closest('[data-zshop-jump-to]');
      if (!link) return;
      var group = document.getElementById(link.getAttribute('data-zshop-jump-to'));
      if (!group) return;
      e.preventDefault();
      var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* scrollIntoView honours the group's scroll-margin-top, so the shell
         never lands on top of the heading it just scrolled to. */
      group.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      /* Move focus for a keyboard user without a second scroll. */
      group.setAttribute('tabindex', '-1');
      group.focus({ preventScroll: true });
    });

    syncJump(root);
  }

  function pinShopBars() {
    var header = qs('sticky-header') || qs('.section-header');
    var y = 0;
    if (header) {
      var r = header.getBoundingClientRect();
      if (r.bottom > 0) y = Math.round(r.bottom);
    }
    document.documentElement.style.setProperty('--zshop-sticky-top', y + 'px');
    qsa('[data-zshop-holds]').forEach(syncJump);
  }

  var pinQueued = false;
  function pinShopBarsSoon() {
    if (pinQueued) return;
    pinQueued = true;
    requestAnimationFrame(function () {
      pinQueued = false;
      pinShopBars();
    });
  }

  /* The PDP's add does not come through addVariant. Dawn's product-form.js
     posts /cart/add itself and then calls cart.renderContents(), publishing
     cartUpdate on the way past with the add response -- which carries the
     new line's key. Subscribing is the only hook into that path that does
     not mean forking product-form.js.

     Bound in boot(), not at module scope: pubsub.js and this file are both
     deferred, and `subscribe` is not on window until it has run. */
  function bindAddedState() {
    if (window._zshopAddedBound) return;
    if (!window.subscribe || !window.PUB_SUB_EVENTS || !window.PUB_SUB_EVENTS.cartUpdate) return;
    window._zshopAddedBound = true;
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      var key = (addedLine(event.cartData) || {}).key;
      if (!key) return;
      /* renderContents runs immediately after this, so the marker is
         applied by the MutationObserver rather than by paintAdded here. */
      enterAdded(key);
    });
  }

  function boot() {
    bindCart();
    bindAddedState();
    qsa('[data-zshop-tools]').forEach(initTools);
    qsa('[data-zshop-holds]').forEach(initHolds);
    qsa('[data-zshop-holds]').forEach(initJump);
    seedPicks(document);
    pinShopBars();
    if (!window._zshopPin) {
      window._zshopPin = true;
      window.addEventListener('scroll', pinShopBarsSoon, { passive: true });
      window.addEventListener('resize', pinShopBarsSoon);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  window.zshopAdd = addVariant;
})();
